import { useAiAssistantStatusQuery } from '@query';

import { useNavLayout } from '@shared/common/components/NavLayout';
import { GENERAL_URLS } from '@shared/constants/urls';

import { Typography } from '@ui/uikit/components/common/Typography';
import { MagicAi } from '@ui/uikit/components/icons/poly/MagicAi';

import { MarkdownOverrideLink } from '../ChatDialog/screens/ChatScreen/AiMessage/MarkdownOverrides/MarkdownOverrideLink';
import { HistoryScreen } from '../ChatDialog/screens/HistoryScreen/HistoryScreen';
import { NewChatScreen } from '../ChatDialog/screens/NewChatScreen/NewChatScreen';
import { AiAgentScreens } from './ChatDialog';
import { BalanceScreen } from './screens/BalanceScreen/BalanceScreen';
import { ChatScreen } from './screens/ChatScreen/ChatScreen';

type ChatContentProps = {
  activeScreen: AiAgentScreens;
  backToChat: () => void;
  openNewChat: () => void;
};

export const ChatContent = ({ activeScreen, backToChat, openNewChat }: ChatContentProps) => {
  const { user, onLogin } = useNavLayout();

  const { data, isPending } = useAiAssistantStatusQuery({ enabled: !!user });

  if (!user) {
    return (
      <div className="w-full flex flex-col justify-center items-center gap-6 text-center h-full">
        <span className="msq-btn msq-btn-icon-lg msq-btn-brand-gradient rounded-full">
          <MagicAi className="icon-xs" />
        </span>
        <span className="text-caption-md-a">
          Hello! I'm Mystic, your AI assistant <br />
          for navigating the Web3 world.
        </span>
        <span className="text-caption-md-a">Connect your wallet to get started.</span>
        <button className="msq-btn msq-btn-lg msq-btn-primary" onClick={onLogin}>
          Connect
        </button>
      </div>
    );
  }

  if (!isPending && !data?.isAvailable) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="flex flex-col gap-2 justify-center items-center px-8">
          <p>
            To access the <Typography variant="caption-m-strong">Mystic AI Agent</Typography> and
            unlock the full Magic Square experience, you’ll need to boost your{' '}
            <Typography variant="caption-m-strong">SQRv (Voting Power)</Typography> by staking{' '}
            <Typography variant="caption-m-strong">SQR</Typography> tokens.
          </p>
          <ol className="list-decimal pl-8">
            <li>
              You can stake your tokens{' '}
              <MarkdownOverrideLink href={GENERAL_URLS.staking}>here</MarkdownOverrideLink>.
            </li>
            <li>
              To learn more about SQRv, please see{' '}
              <MarkdownOverrideLink href="https://docs.magicsquare.io/magic-dao/how-to-get-voting-power-in-the-magic-square-dao">
                documentation
              </MarkdownOverrideLink>
              .
            </li>
          </ol>
        </div>
      </div>
    );
  }

  if (activeScreen === 'chat') {
    return <ChatScreen openNewChat={openNewChat} />;
  }

  if (activeScreen === 'newChat') {
    return <NewChatScreen backToChat={backToChat} />;
  }

  if (activeScreen === 'balance') {
    return <BalanceScreen backToChat={backToChat} />;
  }

  if (activeScreen === 'history') {
    return <HistoryScreen backToChat={backToChat} />;
  }
};
